import Controller from '@ember/controller';
import { action, get } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isTesting, macroCondition } from '@embroider/macros';
import { cached, tracked } from '@glimmer/tracking';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { Hotel, Insightpagesfavourite } from 'frontend-utils/models';
import { SHORT_TERM_RENTAL_BANNER_TOKEN } from 'frontend-utils/utils/constants/constant-values';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import { convertKilometersToMiles } from 'frontend-utils/utils/distance';
import type { Dropdown } from 'lumen/components/general/dropdowns/dropdown-yield';
import type {
  Event as LighthouseEvent,
  Holiday,
  Occupancy,
  Period,
  ShortTermRentalRate,
} from 'lumen/models';
import type HotelShortTermRentalRoute from 'lumen/routes/hotel/short-term-rental';
import type { Filters } from 'lumen/routes/hotel/short-term-rental';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  DATE_AGGREGATION,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';
import {
  AL_REF_DAYS_AGO_VALUES,
  DEFAULT_NUMBER_OF_DAYS,
  SHORT_TERM_RENTAL_DATEPICKER_MIN_DATE,
} from 'lumen/utils/constants/short-term-rental-constants';
import { isGoogleMapsAvailable } from 'lumen/utils/google-maps';
import type { ModelFrom } from 'lumen/utils/routes';
import additionalFeesTranslation from 'lumen/utils/short-term-rental-utils';

type SortedData = Record<
  string,
  {
    events?: LighthouseEvent[];
    holidays?: Holiday[];
    occupancy?: Occupancy;
    period?: Period;
    rentalOccupancy?: ShortTermRentalRate;
    rentalRate?: ShortTermRentalRate;
  }
>;

export default class HotelShortTermRentalController extends Controller {
  declare model: ModelFrom<HotelShortTermRentalRoute>;

  queryParams = [
    'fromDate',
    'toDate',
    'radius',
    'refDaysAgo',
    'dateAggregation',
    'activeFilters',
    'priceRangeStart',
    'priceRangeEnd',
  ];

  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare myotbSs: Services['myotb-ss'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked activeFilters: string[] | null = null;
  @tracked dateAggregation: DateAggregationType =
    DEFAULT_QP_VALUES.hotel['short-term-rental'].dateAggregation;
  @tracked fromDate: string | null = null;
  @tracked hasDismissedBanner = false;
  // @ts-expect-error: Property 'localStorage' is used before its initialization
  @tracked isBannerHidden = this.localStorage.getItem(
    SHORT_TERM_RENTAL_BANNER_TOKEN,
  );
  @tracked isEvolutionCardOpen = true;
  @tracked priceRangeEnd: number | null = null;
  @tracked priceRangeStart: number | null = null;
  @tracked radius: number | null = null;
  @tracked refDaysAgo = DEFAULT_QP_VALUES.hotel['short-term-rental'].refDaysAgo;
  @tracked tempPriceRangeEnd: number = this.priceRangeEndWithFallback;
  @tracked tempPriceRangeStart: number = this.priceRangeStartWithFallback;
  @tracked tempRadius: number = this.radiusWithFallback;
  @tracked toDate: string | null = null;

  get activeFiltersWithFallback(): string[] {
    return this.activeFilters ?? this.filters.breakdown_filters;
  }

  get allDefaultFilters(): (number | string)[] {
    const { filters } = this;

    return [
      ...filters.breakdown_filters,
      filters.radius,
      filters.price_range_start,
      filters.price_range_end,
    ];
  }

  get allFilters(): (number | string)[] {
    return [
      ...this.activeFiltersWithFallback,
      this.radiusWithFallback,
      this.priceRangeStartWithFallback,
      this.priceRangeEndWithFallback,
    ];
  }

  get breakdownMetaMessage(): string | undefined {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.model.breakdowns?.value?.meta?.message;
  }

  get currencyCode(): string {
    return this.hotelAndMarketService.hotel.currencyCode;
  }

  get currencySymbol(): string {
    return this.hotelAndMarketService.hotel.currencySymbol;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateAggregationOptions() {
    return [
      {
        id: DATE_AGGREGATION.DAY,
        label: this.intl.t('general.day').toString(),
      },
      {
        id: DATE_AGGREGATION.WEEK,
        label: this.intl.t('general.week').toString(),
      },
    ];
  }

  get datePickerMaxDate(): Dayjs {
    return this.hotelAndMarketService.getLocalTime().add(1, 'year');
  }

  get datePickerMinDate(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(
      SHORT_TERM_RENTAL_DATEPICKER_MIN_DATE,
    );
  }

  get datePickerRange(): { end: Dayjs; start: Dayjs } {
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRangeOptions() {
    const todayMoment = this.hotelAndMarketService.getLocalTime();

    return [
      {
        options: [
          {
            text: 'revenue_insight.current_month',
            id: 'current_month',
            isSelected: false,
            range: {
              start: todayMoment.startOf('month'),
              end: todayMoment.endOf('month'),
            },
          },
          {
            text: 'general.next_days',
            count: 30,
            id: 'thirty_days',
            isSelected: true,
            range: {
              start: todayMoment,
              end: todayMoment.add(30, 'day'),
            },
          },
          {
            text: 'general.next_days',
            count: 60,
            id: 'sixty_days',
            isSelected: false,
            range: {
              start: todayMoment,
              end: todayMoment.add(60, 'day'),
            },
          },
          {
            text: 'general.next_days',
            count: 90,
            id: 'ninety_days',
            isSelected: false,
            range: {
              start: todayMoment,
              end: todayMoment.add(90, 'day'),
            },
          },
          {
            text: 'general.custom',
            id: 'custom',
            isSelected: false,
            range: {
              start: null,
              end: null,
            },
          },
        ],
      },
    ];
  }

  get defaultPriceRangeEnd(): number {
    return this.filters.price_range_end ?? this.maxPrice;
  }

  get defaultPriceRangeStart(): number {
    return this.filters.price_range_start ?? this.minPrice;
  }

  get defaultRadius(): number {
    return this.filters.radius ?? this.maxRadius;
  }

  get events(): LighthouseEvent[] {
    return this.model.evolutions?.value?.events ?? [];
  }

  get filters(): Filters {
    const { defaultFilters } = this.model;

    return defaultFilters![0]!.filters as unknown as Filters;
  }

  get formattedFromDate(): string {
    return this.datePickerRange.start.format('D MMMM YYYY');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedPriceRange() {
    const formattedPriceStart = this.hotelAndMarketService.formatRate(
      this.priceRangeStartWithFallback,
    );

    const formattedPriceEnd = this.hotelAndMarketService.formatRate(
      this.priceRangeEndWithFallback,
    );

    return htmlSafe(`${formattedPriceStart} - ${formattedPriceEnd}`);
  }

  get formattedRadius(): string {
    return this.formatRadius(this.radiusWithFallback);
  }

  get formattedTempRadius(): string {
    return this.formatRadius(this.tempRadius);
  }

  get fromDateMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTimeWithFallback(this.fromDate);
  }

  get hasEditRights(): boolean {
    return this.access.hasShortTermRentalEditRights;
  }

  get hasInvalidSelection(): boolean {
    return this.breakdownMetaMessage === 'properties.invalid';
  }

  get hasInvalidSelectionBanner(): boolean {
    return this.hasInvalidSelection && !this.hasDismissedBanner;
  }

  get hasOnlyDefaultFiltersSelected(): boolean {
    if (this.model.defaultFilters?.length === 0) {
      return false;
    }

    return (
      JSON.stringify(this.allFilters) === JSON.stringify(this.allDefaultFilters)
    );
  }

  get isEvolutionRequestLoading(): boolean {
    return Boolean(this.model.evolutions?.isRunning);
  }

  get isGoogleMapsAPIAvailable(): boolean {
    // Do not render the Google Map in visual tests to prevent timing issues
    if (macroCondition(isTesting())) {
      return false;
    }

    return isGoogleMapsAvailable();
  }

  get isInvalidPriceRangeEnd(): boolean {
    return (
      this.tempPriceRangeStart > this.tempPriceRangeEnd ||
      this.tempPriceRangeEnd > this.maxPrice
    );
  }

  get isInvalidPriceRangeStart(): boolean {
    return (
      this.tempPriceRangeStart > this.tempPriceRangeEnd ||
      this.tempPriceRangeStart < this.minPrice
    );
  }

  get isResetOrRemoveDisabled(): boolean {
    if (this.hasOnlyDefaultFiltersSelected) {
      const currentFilters = [this.maxRadius, this.minPrice, this.maxPrice];

      return JSON.stringify(currentFilters) === JSON.stringify(this.allFilters);
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mappedFilters() {
    return this.activeFiltersWithFallback.map((filter) => {
      const [type, subType] = filter.split('.') as [string, string];

      let displayValue = this.intl.t(
        `short_term_rental.breakdowns.${type}.${subType}`,
      );

      if (type === 'additional_fees') {
        displayValue = additionalFeesTranslation(
          subType,
          this.hotelAndMarketService,
          this.intl,
        );
      }

      return {
        key: `${type}.${subType}`,
        values: [
          {
            displayValue,
            isNew: true,
          },
        ],
        translationKey: `short_term_rental.breakdowns.${type}`,
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxPrice() {
    // @ts-expect-error: Object is possibly 'null'.
    return this.model.config.priceRangeLimitMax;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxRadius() {
    // @ts-expect-error: Object is possibly 'null'.
    return this.model.config.radiusLimit;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minPrice() {
    // @ts-expect-error: Object is possibly 'null'.
    return this.model.config.priceRangeLimitMin;
  }

  get priceRangeEndWithFallback(): number {
    return this.priceRangeEnd ?? this.defaultPriceRangeEnd;
  }

  get priceRangeStartWithFallback(): number {
    return this.priceRangeStart ?? this.defaultPriceRangeStart;
  }

  get radiusFromHotelText(): string {
    return `${this.formattedRadius} ${this.intl.t('short_term_rental.from_hotel')}`;
  }

  get radiusWithFallback(): number {
    return this.radius ?? this.defaultRadius;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refDaysAgoSelectionOptions() {
    const { yesterday, threeDaysAgo, sevenDaysAgo, fourteenDaysAgo } =
      AL_REF_DAYS_AGO_VALUES;

    return [
      {
        id: yesterday,
        label: this.intl.t('general.daysago', { count: yesterday }),
      },
      {
        id: threeDaysAgo,
        label: this.intl.t('general.daysago', { count: threeDaysAgo }),
      },
      {
        id: sevenDaysAgo,
        label: this.intl.t('general.daysago', { count: sevenDaysAgo }),
      },
      {
        id: fourteenDaysAgo,
        label: this.intl.t('general.daysago', { count: fourteenDaysAgo }),
      },
    ];
  }

  get resetOrRemoveFiltersText(): string {
    if (this.hasOnlyDefaultFiltersSelected) {
      return this.intl.t('general.remove');
    }

    return this.intl.t('general.reset');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedDateAggregation() {
    return this.dateAggregationOptions.find(
      (option) => option.id === this.dateAggregation,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRefDaysAgo() {
    return this.refDaysAgoSelectionOptions.find(
      (refDaysAgoOption) => refDaysAgoOption.id === this.refDaysAgo,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shortTermRentalLink() {
    return {
      text: this.intl.t('general.learn_more'),
      url: `https://help.${DOMAIN_NAME}/en/articles/6952013-what-is-short-term-rental`,
      icon: true,
    };
  }

  get singleDaySelected(): boolean {
    return this.fromDateMoment.isSame(this.toDateMoment, 'day');
  }

  // Sort the model rates and occupancies and the short-term rental rates and occupancies based on the extractDate
  get sortedData(): SortedData {
    const sortedData: SortedData = {};

    if (this.isEvolutionRequestLoading) {
      return sortedData;
    }

    const { events, holidays, occupancies, periods, shortTermRentalRates } =
      this.model.evolutions.value!;

    const rentalRates = shortTermRentalRates.filter(
      ({ modelName }) => modelName === 'short-term-rental-rate',
    );

    const rentalOccupancies = shortTermRentalRates.filter(
      ({ modelName }) => modelName === 'short-term-rental-occupancy',
    );

    const today = this.hotelAndMarketService
      .getLocalTime()
      .format('YYYY-MM-DD');

    const endDate = this.singleDaySelected
      ? dayjs
          .min(dayjs(), dayjs(today), dayjs(this.model.endDate))
          .format('YYYY-MM-DD')
      : this.model.endDate;

    if (this.singleDaySelected) {
      periods.forEach((period) => {
        sortedData[period.formattedExtractDate] = {
          period,
        };
      });

      if (this.access.hasOTB) {
        occupancies!.forEach((occupancy) => {
          sortedData[occupancy.period.formattedExtractDate] = {
            ...sortedData[occupancy.period.formattedExtractDate],
            occupancy,
          };
        });
      }

      rentalOccupancies.forEach((occupancy) => {
        sortedData[occupancy.formattedExtractDate] = {
          ...sortedData[occupancy.formattedExtractDate],
          rentalOccupancy: occupancy,
          events: [],
          holidays: [],
        };
      });

      rentalRates.forEach((rate) => {
        sortedData[rate.formattedExtractDate] = {
          ...sortedData[rate.formattedExtractDate],
          rentalRate: rate,
        };
      });
    } else {
      periods.forEach((period) => {
        sortedData[period.day.formattedDate] = {
          period,
        };
      });

      if (this.access.hasOTB) {
        occupancies!.forEach((occupancy) => {
          // Ember Proxy Object: Since Ember 3.1, this is usually fine as you no longer need to use `.get()` to access computed properties. However, in this case, the object in question is a special kind of Ember object (a proxy). Therefore, it is still necessary to use `.get('formattedDate')` in this case.
          // @ts-expect-error: Type 'unknown' cannot be used as an index type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, ember/no-get
          sortedData[get(occupancy, 'day.formattedDate')] = {
            // Ember Proxy Object: Since Ember 3.1, this is usually fine as you no longer need to use `.get()` to access computed properties. However, in this case, the object in question is a special kind of Ember object (a proxy). Therefore, it is still necessary to use `.get('formattedDate')` in this case.
            // @ts-expect-error: Type 'unknown' cannot be used as an index type
            // eslint-disable-next-line ember/no-get
            ...sortedData[get(occupancy, 'day.formattedDate')],
            occupancy,
          };
        });
      }

      rentalOccupancies.forEach((occupancy) => {
        // Ember Proxy Object: Since Ember 3.1, this is usually fine as you no longer need to use `.get()` to access computed properties. However, in this case, the object in question is a special kind of Ember object (a proxy). Therefore, it is still necessary to use `.get('formattedDate')` in this case.
        // @ts-expect-error: Type 'unknown' cannot be used as an index type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, ember/no-get
        sortedData[get(occupancy, 'day.formattedDate')] = {
          // Ember Proxy Object: Since Ember 3.1, this is usually fine as you no longer need to use `.get()` to access computed properties. However, in this case, the object in question is a special kind of Ember object (a proxy). Therefore, it is still necessary to use `.get('formattedDate')` in this case.
          // @ts-expect-error: Type 'unknown' cannot be used as an index type
          // eslint-disable-next-line ember/no-get
          ...sortedData[get(occupancy, 'day.formattedDate')],
          rentalOccupancy: occupancy,
          events: [],
          holidays: [],
        };
      });

      rentalRates.forEach((rate) => {
        // Ember Proxy Object: Since Ember 3.1, this is usually fine as you no longer need to use `.get()` to access computed properties. However, in this case, the object in question is a special kind of Ember object (a proxy). Therefore, it is still necessary to use `.get('formattedDate')` in this case.
        // @ts-expect-error: Type 'unknown' cannot be used as an index type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, ember/no-get
        sortedData[get(rate, 'day.formattedDate')] = {
          // Ember Proxy Object: Since Ember 3.1, this is usually fine as you no longer need to use `.get()` to access computed properties. However, in this case, the object in question is a special kind of Ember object (a proxy). Therefore, it is still necessary to use `.get('formattedDate')` in this case.
          // @ts-expect-error: Type 'unknown' cannot be used as an index type
          // eslint-disable-next-line ember/no-get
          ...sortedData[get(rate, 'day.formattedDate')],
          rentalRate: rate,
        };
      });

      events.forEach((event) => {
        if (event.isAccepted) {
          event.days.forEach((eventDay) => {
            if (
              this.model.startDate! <= eventDay.formattedDate &&
              eventDay.formattedDate <= endDate!
            ) {
              const date =
                this.dateAggregation === DATE_AGGREGATION.WEEK
                  ? eventDay.date.startOf('week').format('YYYY-MM-DD')
                  : eventDay.formattedDate;

              if (Object.keys(sortedData).includes(date)) {
                sortedData[date] = {
                  ...sortedData[date]!,
                  events: [...sortedData[date]!.events!, event],
                };
              }
            }
          });
        }
      });

      holidays!.forEach((holiday) => {
        holiday.days.forEach((holidayDay) => {
          if (
            this.model.startDate! <= holidayDay.formattedDate &&
            holidayDay.formattedDate <= endDate!
          ) {
            const date =
              this.dateAggregation === DATE_AGGREGATION.WEEK
                ? holidayDay.date.startOf('week').format('YYYY-MM-DD')
                : holidayDay.formattedDate;

            if (Object.keys(sortedData).includes(date)) {
              sortedData[date] = {
                ...sortedData[date]!,
                holidays: [...sortedData[date]!.holidays!, holiday],
              };
            }
          }
        });
      });
    }

    return sortedData;
  }

  get toDateMoment(): Dayjs {
    if (this.toDate) {
      return this.hotelAndMarketService.toLocalTime(this.toDate);
    }

    return this.hotelAndMarketService
      .getLocalTime()
      .add(DEFAULT_NUMBER_OF_DAYS, 'day');
  }

  formatRadius(kilometers: number): string {
    if (this.hotelAndMarketService.useImperialSystem) {
      return `${Math.round(convertKilometersToMiles(kilometers))} mi`;
    }

    return `${kilometers} km`;
  }

  @action
  addFilter(item: {
    isDisabled?: boolean;
    subType: string;
    type: string;
  }): void {
    const newFilters = this.activeFiltersWithFallback;
    const { isDisabled, type, subType } = item;

    if (isDisabled) {
      return;
    }

    const key = `${type}.${subType}`;
    const index = newFilters.indexOf(key);

    if (index !== -1) {
      newFilters.splice(index, 1);
    } else {
      newFilters.push(key);
    }

    this.router.transitionTo({
      queryParams: {
        activeFilters: newFilters,
      },
    });

    this.hasDismissedBanner = false;
  }

  @action
  applyPriceChanges(dropdown: Dropdown): void {
    if (this.isInvalidPriceRangeStart || this.isInvalidPriceRangeEnd) {
      return;
    }

    this.router.transitionTo({
      queryParams: {
        priceRangeStart: this.tempPriceRangeStart,
        priceRangeEnd: this.tempPriceRangeEnd,
      },
    });

    dropdown.toggleDropdown();
  }

  @action
  applyRadiusChanges(dropdown: Dropdown): void {
    this.router.transitionTo({
      queryParams: {
        radius: this.tempRadius,
      },
    });

    dropdown.toggleDropdown();
  }

  @action
  cancelChanges(dropdown: Dropdown): void {
    this.tempRadius = this.radiusWithFallback;
    this.tempPriceRangeStart = this.priceRangeStartWithFallback;
    this.tempPriceRangeEnd = this.priceRangeEndWithFallback;

    dropdown.toggleDropdown();
  }

  @action
  clearActiveFilter(filterKey: string): void {
    const newFilters = this.activeFiltersWithFallback;

    const indexToDelete = newFilters.findIndex(
      (filter) => filter === filterKey,
    );

    if (indexToDelete !== -1) {
      newFilters.splice(indexToDelete, 1);

      this.router.transitionTo({
        queryParams: {
          activeFilters: newFilters,
        },
      });
    }

    this.hasDismissedBanner = false;
  }

  @action
  closeShortTermRentalBanner(): void {
    // @ts-expect-error: Type 'boolean' is not assignable to 'string | null'
    this.isBannerHidden = true;

    this.localStorage.setItem(
      SHORT_TERM_RENTAL_BANNER_TOKEN,
      JSON.stringify(false),
    );
  }

  @action
  dismissBanner(): void {
    this.hasDismissedBanner = true;
  }

  @action
  onCancel(): void {
    this.tempRadius = this.radiusWithFallback;
  }

  @action
  onRefDaysAgoSelectionChanged(selectedReference: { id: number }): void {
    this.router.transitionTo({
      queryParams: {
        refDaysAgo: selectedReference.id,
      },
    });
  }

  @action
  rangeChanged(range: { end: Dayjs; start: Dayjs }): void {
    const queryParams: Record<string, unknown> = {};

    if (range.start) {
      queryParams['fromDate'] = range.start.format('YYYY-MM-DD');
    }

    if (range.end) {
      queryParams['toDate'] = range.end.format('YYYY-MM-DD');
    } else {
      queryParams['toDate'] = range.start.format('YYYY-MM-DD');
    }

    if (dayjs(range.end).diff(dayjs(range.start), 'day') > 30) {
      queryParams['dateAggregation'] = DATE_AGGREGATION.WEEK;
    } else {
      queryParams['dateAggregation'] = DATE_AGGREGATION.DAY;
    }

    this.router.transitionTo({ queryParams });
  }

  @action
  removeAllFilters(): void {
    const queryParams = {
      radius: this.maxRadius,
      priceRangeStart: this.minPrice,
      priceRangeEnd: this.maxPrice,
      activeFilters: [],
    };

    this.router.transitionTo({ queryParams });
  }

  @action
  resetFiltersToDefault(): void {
    const queryParams = {
      radius: this.defaultRadius,
      priceRangeStart: this.defaultPriceRangeStart,
      priceRangeEnd: this.defaultPriceRangeEnd,
      activeFilters: [...this.filters.breakdown_filters],
    };

    this.router.transitionTo({ queryParams });
  }

  @action
  resetOrRemoveFilters(): void {
    if (this.hasOnlyDefaultFiltersSelected) {
      this.removeAllFilters();
    } else {
      this.resetFiltersToDefault();
    }
  }

  @action
  setDateAggregation(dateAggregation: { id: DateAggregationType }): void {
    this.dateAggregation = dateAggregation.id;

    this.router.transitionTo({
      queryParams: {
        dateAggregation: this.dateAggregation,
      },
    });
  }

  @action
  setDefault(): void {
    const defaultFilters = this.model.defaultFilters!;
    let objectToSave: Insightpagesfavourite;

    if (defaultFilters.length > 0) {
      defaultFilters[0]!.filters = {
        // @ts-expect-error: Incorrect type
        breakdown_filters: this.activeFiltersWithFallback,
        radius: this.radiusWithFallback,
        price_range_start: this.priceRangeStartWithFallback,
        price_range_end: this.priceRangeEndWithFallback,
      };

      objectToSave = defaultFilters[0]!;
    } else {
      objectToSave = this.store.createRecord<Insightpagesfavourite>(
        'insightpagesfavourite',
        {
          name: 'default',
          hotel: this.hotelAndMarketService.hotelOrMarket as Hotel,
          pageType: 'RENTALS',
          filters: {
            // @ts-expect-error: Incorrect type
            breakdown_filters: this.activeFiltersWithFallback,
            radius: this.radiusWithFallback,
            price_range_start: this.priceRangeStartWithFallback,
            price_range_end: this.priceRangeEndWithFallback,
          },
        },
      );
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      objectToSave.save().then(() => {
        this.toast.success('', {
          title: this.intl.t('general.toast.successfully_saved'),
          timeout: TOAST_TIMEOUT_LONG,
          showIcon: true,
        });
      });
    } catch (error) {
      this.toast.alert('', {
        title: this.intl.t('general.error_title'),
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
      this.errorTracking.captureException(error);
    }
  }

  @action
  setPriceRangeEnd(event: Event): void {
    this.tempPriceRangeEnd = Number(
      (event.target as HTMLInputElement).value.replace(/[^0-9]/g, ''),
    );
  }

  @action
  setPriceRangeStart(event: Event): void {
    this.tempPriceRangeStart = Number(
      (event.target as HTMLInputElement).value.replace(/[^0-9]/g, ''),
    );
  }

  @action
  toggleEvolutionCard(): void {
    this.isEvolutionCardOpen = !this.isEvolutionCardOpen;
  }

  @action
  updateRadius(event: Event): void {
    // @ts-expect-error: Type 'string' is not assignable to 'number'
    this.tempRadius = (event.target as HTMLInputElement).value;
  }
}
